import { isPersonalEmail } from '../data/webinarForm'

export interface RegistrationAnswers {
  email: string
  institutionType: string
  seniority: string
  timeline: string
  decisionRole: string
  functionArea: string
  briefingRequested: boolean
}

export type Lane = 'hot' | 'warm' | 'nurture'

export interface Scored {
  /** Out of one hundred. */
  total: number
  breakdown: { dimension: string; score: number; max: number }[]
  lane: Lane
  /** The service line the follow up offer should lead with. */
  followUpLine: string
  /** True where the contact is a partner rather than a prospective buyer. */
  partnership: boolean
  personalEmail: boolean
}

/** Institution fit, 0 to 30. Finance, planning, revenue and partners score highest. */
function institutionScore(type: string): number {
  switch (type) {
    case 'Ministry (national)':
      return 30
    case 'Central bank or revenue authority':
      return 30
    case 'Development partner, DFI or donor':
      return 28
    case 'Multilateral or UN body':
      return 24
    case 'Government agency, authority or commission':
      return 22
    case 'Subnational or local government':
      return 16
    case 'State owned enterprise':
      return 12
    case 'Nonprofit working with government':
      return 10
    default:
      return 4
  }
}

/** Seniority, 0 to 25. */
function seniorityScore(level: string): number {
  switch (level) {
    case 'Minister, deputy minister or permanent secretary':
      return 25
    case 'Director general or agency head':
      return 23
    case 'Director or head of unit':
      return 18
    case 'Manager or coordinator':
      return 12
    case 'Officer or specialist':
      return 7
    case 'Adviser or consultant':
      return 5
    default:
      return 0
  }
}

/** Intent and timeline, 0 to 25. */
function timelineScore(timeline: string): number {
  switch (timeline) {
    case 'Yes, actively':
      return 25
    case 'Yes, exploring':
      return 15
    case 'Just learning for now':
      return 4
    default:
      return 0
  }
}

/** Decision role, 0 to 20. */
function decisionScore(role: string): number {
  switch (role) {
    case 'I decide or approve':
      return 20
    case 'I advise or recommend':
      return 13
    case 'I am researching for others':
      return 5
    default:
      return 0
  }
}

/**
 * The follow up offer is chosen by function, not by seniority, because the
 * right next conversation depends on the work the person actually owns.
 */
export function followUpLineFor(functionArea: string, institutionType: string): string {
  if (
    institutionType === 'Development partner, DFI or donor' ||
    institutionType === 'Multilateral or UN body'
  ) {
    return 'Partnership conversation'
  }
  switch (functionArea) {
    case 'Finance, budget or revenue':
    case 'Monitoring, evaluation or statistics':
    case 'Executive or cabinet':
      return 'Cavalla Gov'
    case 'Policy or planning':
      return 'Cavalla Intelligence'
    case 'Digital, ICT or data':
      return 'Cavalla AI'
    case 'Communications or investment promotion':
      return 'Cavalla Growth'
    default:
      return 'Cavalla Gov'
  }
}

/**
 * Scores a registration out of one hundred and sorts it into a lane.
 *
 * This runs in the browser only so the team can see the result while testing.
 * HubSpot holds the authoritative score: anything posted from a browser can be
 * altered, so the server route must re score from the stored answers.
 */
export function scoreRegistration(answers: RegistrationAnswers): Scored {
  const breakdown = [
    { dimension: 'Institution fit', score: institutionScore(answers.institutionType), max: 30 },
    { dimension: 'Seniority', score: seniorityScore(answers.seniority), max: 25 },
    { dimension: 'Intent and timeline', score: timelineScore(answers.timeline), max: 25 },
    { dimension: 'Decision role', score: decisionScore(answers.decisionRole), max: 20 },
  ]

  const total = breakdown.reduce((sum, item) => sum + item.score, 0)
  const personalEmail = isPersonalEmail(answers.email)

  let lane: Lane
  if (answers.briefingRequested || total >= 70) {
    lane = 'hot'
  } else if (total >= 40) {
    lane = 'warm'
  } else {
    lane = 'nurture'
  }
  // A personal address is never promoted out of nurture on score alone, unless
  // a briefing was asked for outright.
  if (personalEmail && !answers.briefingRequested) lane = 'nurture'

  const followUpLine = followUpLineFor(answers.functionArea, answers.institutionType)

  return {
    total,
    breakdown,
    lane,
    followUpLine,
    partnership: followUpLine === 'Partnership conversation',
    personalEmail,
  }
}

export const LANE_ACTIONS: Record<Lane, string> = {
  hot: 'Routed to the team for the same day, with the Cavalla Gov demonstration link.',
  warm: 'Public sector nurture, with an invitation to the June demonstration session.',
  nurture: 'Kept on the monthly list.',
}
